import React, { useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  ChartControlsProps,
  ChartConGeoChartControls,
} from '../../../redux/ChartPoperties/ChartControlsInterface';
import { ChartsMapStateToProps, ChartsReduxStateProps } from '../ChartsCommonInterfaces';
import {
  interpolateColor,
  checkUidPresenceInCheckedMeasureForTooltip,
} from '../../CommonFunctions/CommonFunctions';
import { getGeoJSON } from './GeoJSON/MapCommonFunctions';
import { palette } from '../../..';
import { updateHoveredItems } from '../../../redux/TabTile/TabActions';
import { tooltipStyle } from '../../Common/EChartTooltip';
import { RootState } from '../../../redux';
import { aspectScaleMap } from './Components/MapCommonComponents';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { updateGeoChartStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { formatNumberWithAbbrev } from '../../ChartOptions/Format/NumberFormatter';
import { LABEL_THRESHOLDS, LEGEND_THRESHOLDS } from '../../ChartOptions/ChartThresholds';

const FilledMap = ({
  //props
  propKey,
  graphDimension,
  colorScheme,
  setMapJSON = () => {},
  setLoading = () => {},
  chartArea,
  fontFamily,
  graphTileSize,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  var type = chartProperties.properties[propKey].Geo.geoMapKey;

  const { tooltipFormats, chartAxes, isTooltipEnabled, stateCheckBox, tabState, selectedTab } =
    useSelector((state: RootState) => ({
      tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
      chartAxes: state.chartProperties.properties[propKey]?.chartAxes,
      isTooltipEnabled:
        state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
      stateCheckBox: state.chartControls?.properties[propKey]?.labelOptions?.stateCheckBox,
      tabState: state.tabState,
      selectedTab: state.tabTileProps.selectedTabId,
    }));

  var chartControl: ChartControlsProps = chartControls.properties[propKey];

  var geoStyle: ChartConGeoChartControls = chartControls.properties[propKey].geoChartControls || {};

  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];
  let mapData: any[] = [];
  let _dimensionField = chartProperties.properties[propKey].chartAxes[1];
  let _measureField = chartProperties.properties[propKey].chartAxes[2];
  let keyName = _dimensionField.fields[0]?.displayname;
  let valueName = _measureField.fields[0]?.displayname;
  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || (chartArea === 'dashboard' && !geoStyle.manualColor)) {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const prevValueNameRef = useRef<string | undefined>(undefined);
  const selectedRangeRef = useRef<[number, number] | null>(null);
  const dispatch = useDispatch();
  const hasMeasureChanged = prevValueNameRef.current !== valueName;
  const [options, setOptions] = useState({});
  // const [mapLoading, setMapLoading] = useState<boolean>(false);
  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);
  const [mapJSON, setMapGeoJSON] = useState<any>('');

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  async function registerGeoMap(name: string) {
    //https://code.highcharts.com/Zurich/
    //https://github.com/adarshbiradar/maps-geojson/tree/master/states
    var ROOT_PATH = 'https://echarts.apache.org/examples';

    // setLoading(true);
    // setMapLoading(true);
    let geoJSON = await getGeoJSON(name, setLoading);
    setMapJSON(geoJSON); // for MapJSON used at other places like chartAxes
    setMapGeoJSON(geoJSON); // for MapJSON used here
    setLoading(false);
    // setMapLoading(false);
  }

  useEffect(() => {
    if (mapJSON?.features)
      echarts.registerMap(chartProperties.properties[propKey].Geo.geoLocation, mapJSON, {});
  }, [mapJSON, chartProperties.properties[propKey].Geo.geoLocation]);

  useEffect(() => {
    (async () => {
      await registerGeoMap(chartProperties.properties[propKey].Geo.geoLocation);
    })();
  }, [chartProperties.properties[propKey].Geo.geoLocation]);

  // useEffect(() => {
  //   dispatch(updateLabelOption(propKey, 'showLabel', false));
  // }, []);
  // const formatMinMaxValue = (
  //   value: number,
  //   separator: string,
  //   formatType: 'Number' | 'Currency' | 'Percent' = 'Number',
  //   enableRounding: boolean,
  //   roundingDigits: number,
  // ) => {
  //   if (isNaN(value)) return value;

  //   const cleanValue = Number.isInteger(value) ? value : parseFloat(value.toFixed(0));

  //   switch (separator) {
  //     case 'Comma':
  //       return value.toLocaleString();

  //     case 'Abbrev':
  //       if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  //       if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(1) + 'K';
  //       return value.toString();

  //     case 'None':
  //     default:
  //       return cleanValue.toString();
  //   }
  // };
  const formatMinMaxValue = (
    value: number,
    separator: 'None' | 'Comma' | 'Abbrev',
    formatType: 'Number' | 'Currency' | 'Percent' = 'Number',
    enableRounding: boolean,
    roundingDigits: number,
  ) => {
    const currencySymbol = geoStyle.currencySymbol ?? '$';
    const percentType = geoStyle.percentType ?? 'NonCalculated';

    if (isNaN(value)) return value.toString();

    const safeRoundingDigits = Math.min(Math.max(roundingDigits, 0), 4);

    // const roundedValue = enableRounding
    //   ? Math.round(value)
    //   : Number(value.toFixed(safeRoundingDigits));

    const workingValue =
      formatType === 'Percent' && percentType === 'Calculated' ? value * 100 : value;

    const roundedValue = !enableRounding
      ? Math.round(workingValue)
      : Number(workingValue.toFixed(safeRoundingDigits));

    let formatted: string;

    switch (separator) {
      case 'Comma':
        formatted = roundedValue.toLocaleString(undefined, {
          minimumFractionDigits: enableRounding ? 0 : safeRoundingDigits,
          maximumFractionDigits: enableRounding ? 0 : safeRoundingDigits,
        });
        break;

      case 'Abbrev':
        formatted = formatNumberWithAbbrev(roundedValue, enableRounding ? 0 : safeRoundingDigits);
        break;

      case 'None':
      default:
        formatted = enableRounding
          ? roundedValue.toString()
          : roundedValue.toFixed(safeRoundingDigits);
    }

    switch (formatType) {
      case 'Percent':
        return `${formatted} %`;

      case 'Currency':
        return `${currencySymbol} ${formatted}`;

      default:
        return formatted;
    }
  };

  const convertIntoMapData = () => {
    if (chartData && chartData.length > 0) {
      let keyNameArray: string[] = [];
      let matchingMapJSONArray: any = [];

      chartData.forEach((item) => {
        const val = item[keyName];
        if (
          val !== null &&
          val !== undefined &&
          String(val).trim() !== '' &&
          String(val) !== '(Blank)'
        ) {
          keyNameArray.push(val.toString().trim());
        }
      });

      mapJSON.features?.forEach((item: any) => {
        if (keyNameArray.includes(item.properties[type])) {
          matchingMapJSONArray.push({
            key: item.properties[type],
            name: item.properties['name'] || item.properties[type],
          });
        }
      });

      //  Map Data (CHANGED: map -> flatMap)
      // We use flatMap so one data row (Asia) can spawn multiple map rows (China, India, etc.)
      mapData = chartData
        ?.filter((item) => {
          const val = item[keyName];
          return (
            val !== null &&
            val !== undefined &&
            String(val).trim() !== '' &&
            String(val) !== '(Blank)'
          );
        })
        .flatMap((item) => {
          // Find ALL matches in our lookup list, not just the first one
          const matches = matchingMapJSONArray.filter(
            (match: any) => match.key === item[keyName]?.toString().trim(),
          );

          if (matches.length > 0) {
            // Return one row for every country found in that group
            return matches.map((match: any) => ({
              name: match.name,
              value: item[valueName] || 0,
              key: item[keyName],
            }));
          } else {
            // Return the unmatched item (name undefined) to be fixed in the next step
            return [
              {
                name: undefined,
                value: item[valueName] || 0,
                key: item[keyName],
              },
            ];
          }
        });

      // Manual Fixes (CHANGED: Support 1-to-Many fixes)
      if (chartProperties.properties[propKey].Geo.unMatchedChartData?.length > 0) {
        const unMatchedFixes = chartProperties.properties[propKey].Geo.unMatchedChartData;
        let solvedMapData: any[] = [];

        mapData.forEach((dataItem: any) => {
          // Checking if this specific item has a manual fix
          const fix = unMatchedFixes.find(
            (f: any) => f.selectedKey !== '' && f[keyName] == dataItem.key,
          );

          if (fix) {
            let selectedKeyClean = fix.selectedKey.includes(';')
              ? fix.selectedKey.split(';')[1]?.trim()
              : fix.selectedKey;

            // Find ALL GeoJSON features that match the user's selected fix
            const matchedFeatures = mapJSON.features?.filter(
              (f: any) => f.properties[type] === selectedKeyClean,
            );

            if (matchedFeatures && matchedFeatures.length > 0) {
              // Add a row for EVERY country that matches the fix
              matchedFeatures.forEach((feat: any) => {
                solvedMapData.push({
                  ...dataItem,
                  name: feat.properties.name, // Use the real country name ECharts needs
                });
              });
            } else {
              // Fallback: If no features found, just use the key as the name
              solvedMapData.push({ ...dataItem, name: selectedKeyClean });
            }
          } else {
            solvedMapData.push(dataItem);
          }
        });
        mapData = solvedMapData;
      }
    }
  };

  const calculateDataRange = (data: any[], column: string) => {
    // Safety check: if column doesn't exist in data yet, return default
    if (!column || !data || data.length === 0 || (data[0] && !(column in data[0]))) {
      return { min: 0, max: 100 };
    }

    const valuesArray = data.map((el: any) => Number(el[column]) || 0);
    let minValue = Math.min(...valuesArray);
    let maxValue = Math.max(...valuesArray);

    // If min equals max (single value or all 0s), split them so visualMap works
    if (minValue === maxValue) {
      if (maxValue === 0) {
        maxValue = 100;
      } else {
        minValue = 0;
      }
    }

    return { min: minValue, max: maxValue };
  };

  const calculatedLimits = useMemo(() => {
    return calculateDataRange(chartData, valueName);
  }, [chartData, valueName]);

  const activeMinValue = hasMeasureChanged
    ? calculatedLimits.min
    : geoStyle.minValue !== '' && geoStyle.minValue !== null
      ? Number(geoStyle.minValue)
      : calculatedLimits.min;

  const activeMaxValue = hasMeasureChanged
    ? calculatedLimits.max
    : geoStyle.maxValue !== '' && geoStyle.maxValue !== null
      ? Number(geoStyle.maxValue)
      : calculatedLimits.max;
  const isInverted = activeMinValue > activeMaxValue;
  const visualMin = isInverted ? activeMaxValue : activeMinValue;
  const visualMax = isInverted ? activeMinValue : activeMaxValue;

  useEffect(() => {
    if (chartData.length > 0 && valueName) {
      if (prevValueNameRef.current !== valueName) {
        // Only dispatching if data is valid
        if (chartData[0] && valueName in chartData[0]) {
          dispatch(
            updateGeoChartStyleOptions(propKey, 'minValue', calculatedLimits.min.toFixed(2)),
          );
          dispatch(
            updateGeoChartStyleOptions(propKey, 'maxValue', calculatedLimits.max.toFixed(2)),
          );
          prevValueNameRef.current = valueName;
        }
      }
    }
  }, [valueName, chartData, propKey, dispatch, calculatedLimits]);

  useEffect(() => {
    //    let mapMinMax: any = getMinAndMaxValue(valueName);
    convertIntoMapData();

    // const dimensionAxis = chartAxes.find((axis: any) => axis.name === 'Location');
    const dimensionAxis = chartAxes[1];

    const currentTheme = chartThemes[0];
    const { unMatchedChartData = [] } = geoStyle;

    const useDivergent = geoStyle.isDivergentModeEnabled || geoStyle.manualColor;

    //  Helper to remove alpha 'ff' from the end of hex strings
    const hexRemovehelper = (color: any) => {
      if (typeof color === 'string' && color.startsWith('#') && color.length === 9) {
        return color.slice(0, 7);
      }
      return color;
    };

    const rawStartColor =
      geoStyle.manualColor && geoStyle.minColor && geoStyle.minColor !== ''
        ? geoStyle.minColor
        : selectedTheme?.colors?.[1] || '#AF99DB';

    const rawEndColor =
      geoStyle.manualColor && geoStyle.maxColor && geoStyle.maxColor !== ''
        ? geoStyle.maxColor
        : selectedTheme?.colors?.[0] || '#2BB9BB';

    const startColor = hexRemovehelper(rawStartColor);
    const endColor = hexRemovehelper(rawEndColor);
    const finalStart = isInverted ? endColor : startColor;
    const finalEnd = isInverted ? startColor : endColor;

    const stateNames = mapData.filter((item) => item?.name).map((item) => item.name.trim());
    const separator = geoStyle.isDivergentFormat || 'None';
    const formatType = geoStyle.divergentFormatValue || 'Number';
    const enableRounding = geoStyle.enableRounding ?? true;
    const roundingDigits = geoStyle.roundingDigits ?? 1;
    const currencySymbol = geoStyle.currencySymbol ?? '$';

    const resolvedTextColor = chartControl.labelOptions.labelColor
      ? chartControl.labelOptions.labelColor
      : selectedTheme?.dark
        ? '#ffffff'
        : '#757474';

    // Define visualMap only when the switch is ON
    const visualMapConfig = useDivergent
      ? {
          // left: 'right',
          right: 0,
          min: visualMin,
          max: visualMax,
          inRange: {
            color: interpolateColor(finalStart, finalEnd, 20),
          },
          ...(selectedRangeRef.current ? { range: selectedRangeRef.current } : {}),
          text: ['Max', 'Min'],
          textStyle: {
            color: resolvedTextColor,
          },
          fontFamily: fontFamily,
          calculable: true,
          itemHeight: graphDimension.height / 3.3,
          show:
            Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.common.h &&
            Math.floor(graphDimension.width) > LEGEND_THRESHOLDS.common.w &&
            chartData.length > 0
              ? geoStyle.showVisualScale
              : false,
          formatter: (value: number) =>
            formatMinMaxValue(value, separator, formatType, enableRounding, roundingDigits),
        }
      : {
          inRange: {
            color: [selectedTheme?.colors[0]],
          },
          show: false,
        };

    if (
      mapJSON.features &&
      chartProperties.properties[propKey].Geo.unMatchedChartData &&
      echarts.getMap(chartProperties.properties[propKey].Geo.geoLocation)
    ) {
      setOptions({
        geo: {
          map: chartProperties.properties[propKey].Geo.geoLocation,
          silent: false,
          aspectScale:
            geoStyle.aspectScale *
              aspectScaleMap[chartProperties.properties[propKey].Geo.geoLocation] -
              0.08 || 1,
          show: true,
          zoom: geoStyle.mapZoom + 0.15,
          // center: selectedCenter,
          emphasis: {
            focus: geoStyle.enableSelfEmphasis ? 'self' : 'normal',
          },
          select: {
            disabled: true,
          },
          label: {
            normal: {
              show:
                Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                  ? chartControl.labelOptions.showLabel
                  : false,
              formatter: (params: any) => {
                if (stateCheckBox) {
                  return stateNames.includes(params.name) ? params.name : '';
                }
                return params.name || '';
              },
              textStyle: {
                color: chartControl.labelOptions.labelColor
                  ? chartControl.labelOptions.labelColor
                  : selectedTheme?.dark
                    ? palette.secondary.contrastText
                    : palette.primary.contrastText,
                backgroundColor: chartControl.labelOptions.labelBackground
                  ? chartControl.labelOptions.labelBackgroundColor
                  : 'transparent',
                borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
                padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
                fontSize: chartControl.labelOptions.fontSize - 4,
                fontFamily: fontFamily,
              },
            },
            emphasis: {
              show:
                Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                  ? chartControl.labelOptions.showLabel
                  : false,
              textStyle: {
                color: chartControl.labelOptions.labelColor
                  ? chartControl.labelOptions.labelColor
                  : null,
                // backgroundColor: geoStyle.emphasisAreaColor,
                fontSize: chartControl.labelOptions.fontSize - 4,
              },
            },
          },
          itemStyle: {
            normal: {
              areaColor: geoStyle.manualColor
                ? geoStyle.areaColor
                : selectedTheme?.tableHeaderBackground,
              borderColor: geoStyle.manualColor
                ? geoStyle.borderColor
                : selectedTheme?.dark
                  ? palette.secondary.contrastText
                  : 'grey',
              borderWidth: geoStyle.boderWidth,
            },
            emphasis: {
              areaColor: geoStyle.emphasisAreaColor,
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              shadowBlur: 20,
              borderWidth: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          zlevel: 1,
        },
        /* null / {}	*/
        backgroundColor: selectedTheme?.background,
        tooltip:
          dimensionAxis?.fields.length > 0 && isTooltipEnabled
            ? {
                trigger: 'item',
                backgroundColor: 'white',
                borderWidth: 1,
                padding: 0,
                textStyle: {
                  color: '#333',
                  fontSize: 16,
                },
                appendToBody: true,
                extraCssText:
                  'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
                // formatter: function (params: any) {
                //   const filledMapData = params.data || {};
                //   const cityName = filledMapData.key || filledMapData.name;
                //   const measureName = valueName;
                //   const measureValue = filledMapData.value;
                //   const dimensionKey = dimensionAxis?.fields[0]?.displayname;

                //   const tempData: { [key: string]: string | number } = {};
                //   tempData[dimensionKey] = cityName;

                //   if (measureName && measureValue) {
                //     tempData[measureName] = measureValue;
                //   }
                //   //----------remove unchecked measure or any other field value of type date inside tooltip----------//
                //   const _data: { [key: string]: string | number } = {};
                //   Object.keys(tempData).forEach((key: string) => {
                //     const resp = checkUidPresenceInCheckedMeasureForTooltip(
                //       chartAxes,
                //       key,
                //       tooltipFormats?.checkedMeasure,
                //     );
                //     if (resp) {
                //       _data[key] = tempData[key];
                //     }
                //   });
                //   let mergedData = { ..._data };
                //   //--------------get extra response corresponding to a particular dimension------------//
                //   const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
                //   if (tooltipFormats && extraResponses.length > 0) {
                //     const extraFieldObj = extraResponses.find(
                //       (obj: any) =>
                //         String(obj[dimensionKey as string]) === String(filledMapData.key as string),
                //     );
                //     if (extraFieldObj) {
                //       // -------------- remove dimension key value pair -------------- //
                //       const { [dimensionKey as string]: _, ...restExtraFields } = extraFieldObj;
                //       //----------- send final response in tooltip data i.e checked measure plus extra fields ----------//
                //       mergedData = { ...mergedData, ...restExtraFields };
                //     }
                //   }
                //   console.log('extraResponses', extraResponses);
                //   console.log('mergedData', mergedData);
                //   console.log('tooltipFormats', tooltipFormats);
                //   return tooltipStyle(
                //     params.color,
                //     mergedData,
                //     tooltipFormats,
                //     chartAxes,
                //     dimensionKey,
                //     undefined,
                //     undefined,
                //     fontFamily,
                //   );
                // },

                formatter: function (params: any) {
                  const filledMapData = params.data || {};
                  const dimensionKey = chartAxes[1]?.fields[0]?.displayname; // Location / Dimension
                  const measureKey = chartAxes.find((axis: any) => axis.name === 'Measure')
                    ?.fields[0]?.displayname;

                  // Build initial data with only allowed fields
                  const tempData: { [key: string]: string | number } = {};
                  tempData[dimensionKey] = filledMapData.key || filledMapData.name;
                  if (measureKey && filledMapData.value !== undefined) {
                    tempData[measureKey] = filledMapData.value;
                  }

                  // Filter out unchecked measures
                  const _data: { [key: string]: string | number } = {};
                  Object.keys(tempData).forEach((key: string) => {
                    const resp = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      key,
                      tooltipFormats?.checkedMeasure,
                    );
                    if (resp) {
                      _data[key] = tempData[key];
                    }
                  });

                  let mergedData = { ..._data };

                  // Add extra responses if any
                  const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
                  if (tooltipFormats && extraResponses.length > 0) {
                    const extraFieldObj = extraResponses.find(
                      (obj: any) =>
                        String(obj[dimensionKey as string]) === String(filledMapData.key as string),
                    );
                    if (extraFieldObj) {
                      // Remove dimension key from extra fields
                      const { [dimensionKey as string]: _, ...restExtraFields } = extraFieldObj;

                      // Filter unchecked measures in extra fields
                      let filteredFields = { ...mergedData, ...restExtraFields };
                      const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                        chartAxes,
                        measureKey,
                        tooltipFormats?.checkedMeasure,
                      );
                      if (!isPresent) {
                        delete filteredFields[measureKey];
                      }

                      // Final merged data for tooltip
                      mergedData = { ...filteredFields };
                    }
                  }

                  return tooltipStyle(
                    params.color,
                    mergedData,
                    tooltipFormats,
                    chartAxes,
                    dimensionKey,
                    undefined,
                    undefined,
                    fontFamily,
                  );
                },
              }
            : {
                show: isTooltipEnabled,
              },
        visualMap: visualMapConfig,

        series: [
          {
            name: valueName,
            type: 'map',
            roam: true,
            map: chartProperties.properties[propKey].Geo.geoLocation,
            geoIndex: 0,
            data: mapData,
            zlevel: 3,
            dimensions: ['name', 'value'],
          },
        ],
      });
    }
  }, [
    chartControl,
    // chartProperties.properties[propKey].Geo,
    chartProperties.properties[propKey].Geo.unMatchedChartData,
    mapJSON,
    type,
    graphDimension,
    activeMinValue,
    activeMaxValue,
    valueName,
  ]);

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = chartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index,
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  useEffect(() => {
    selectedRangeRef.current = null; // Reset the selected range
  }, [chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('datarangeselected', (e: any) => {
      if (e.selected) {
        selectedRangeRef.current = e.selected as [number, number];
      }
    });

    chartInstance.on('mouseover', (params: any) => {
      if (params?.data?.key && hoveredRef.current?.[chartDataKeys[0]] !== params.data.key) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [chartDataKeys[0]]: params.data.key };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some((item) => item[chartDataKeys[0]] === params.data.key);
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.seriesName) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const RenderChart = () => {
    return (
      <>
        {/* {mapLoading ? <LoadingPopover /> : null} */}
        <ReactEcharts
          ref={chartRef}
          onChartReady={handleChartReady}
          option={options}
          style={{ width: graphDimension.width, height: graphDimension.height }}
        />
      </>
    );
  };

  return <RenderChart />;
};

const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(FilledMap);
